import { defineStore } from "pinia";
import type {
  BusinessBooking,
  ServiceCategory,
  Employee,
  Service,
  Calendar,
  DateTimeFormat,
  Country,
  Booking,
  PaymentMethod,
  Membership,
  ClientMembership,
  Product,
  ProductCategory,
  Deal,
  FetchOrderSummary,
  GiftCard,
  GiftCardSummary,
} from "~/types";

import type { Business, Location } from "~/types/business";

export const useBusinessStore = defineStore("businessStore", () => {
  const business = ref<null | Business>(null);
  const isMenuOpen = ref<boolean>(false);
  const isSearchOpen = ref<boolean>(false);
  const selectedLocation = ref<Location | null>(null);
  const backToProfile = ref<boolean>(false);
  const employees = ref<Employee[]>([]);
  const selectedEmployee = ref<Employee | null>(null);
  const bookingSource = ref<"employee" | "no_employee" | "per_service" | null>(
    null,
  );
  /**
   * The professional chosen for each package member service, keyed by that
   * member service's id. A package is booked as one row per member service, so
   * the choice cannot live on the package itself. Members left out are
   * distributed by occupancy server side.
   */
  const packageEmployees = ref<Record<string, number | string>>({});
  /**
   * Per-member overrides from unpacking a package while editing a booking, keyed
   * by the member's `booking_service_id`. That id — not the service id — is what
   * identifies the occurrence: the same service may also be booked standalone or
   * sit inside another package, and keying by service id would merge them.
   * Members left out follow the professional set on the package as a whole.
   */
  const packageMemberEmployees = ref<Record<string, number | string>>({});
  const categories = ref<ServiceCategory[]>([]);
  const availableCategories = ref<ServiceCategory[]>([]);
  const booking = ref<BusinessBooking>({
    id: null,
    selectedServices: [],
    selectedVariants: [],
    bookedServices: [],
    subTotal: 0,
    date: business.value?.current_date || "",
    paymentMethodId: null,
    tapToken: "",
    summary: null,
    currency: "",
  });
  const calendar = ref<Calendar | null>(null);
  const search = ref<string>("");
  const default_js_formats = ref<DateTimeFormat | null>(null);
  const countries = ref<Country[]>([]);
  const canRefreshServices = ref<boolean>(true);
  const canRefreshConfirm = ref<boolean>(true);
  const canRefreshTimes = ref<boolean>(true);
  const goToCheckout = ref<boolean>(false);
  const goToCheckoutMembership = ref<boolean>(false);
  const cancelBookingOpen = ref<boolean>(false);
  const canceledBooking = ref<null | Booking>(null);
  const bookings = ref<Booking[]>([]);
  const selectedPaymentMethod = ref<null | PaymentMethod>(null);
  const invoice = ref<File | null>(null);
  const creatingNewBooking = ref(false);
  const tapLoading = ref(false);
  const isCardValid = ref(false);
  const isNavigating = ref(false);
  const memberships = ref<Membership[]>([]);
  const backToMemberships = ref(false);
  const canRefreshMemberships = ref(true);
  const selectedMembership = ref<null | Membership>(null);
  const membershipSummary = ref<{
    subtotal: number | string;
    tax: number | string;
    grand_total: number | string;
    membership: Membership;
  } | null>();
  const payMembershipLoading = ref(false);
  const clientMemberships = ref<ClientMembership[]>([]);
  const productCategories = ref<ProductCategory[]>([]);
  const availableProductCategories = ref<ProductCategory[]>([]);
  const goToOrderSummary = ref<boolean>(false);
  const orderSummaryDetails = ref<FetchOrderSummary>();
  const cartCount = ref(0);
  /**
   * The location `cartCount` was last loaded for, or null when it has not been
   * loaded yet. The count alone cannot answer that — an empty cart is
   * legitimately 0 — so without this the route middleware treats every empty
   * cart as "not loaded" and refetches the location details on every
   * navigation, including query-only ones.
   */
  const cartCountLocation = ref<string | null>(null);
  const cartTotal = ref("0.000");
  const payOrderLoading = ref(false);
  const couponApplied = ref(false);
  const deal = ref<null | Deal>(null);
  const finalTotalAfterCoupon = ref<null | string>(null);
  const goToOrderCheckout = ref(false);
  const giftCards = ref<GiftCard[]>([] as GiftCard[]);
  const giftCardSelected = ref<null | GiftCard>(null);
  const canRefreshGiftCards = ref<boolean>(true);
  const currentStep = ref<string>("choose_amount");
  const goToGiftCardSummary = ref<boolean>(false);
  const payGiftCardLoading = ref<boolean>(false);
  const goToGiftCardCheckout = ref<boolean>(false);
  const giftCardSummary = ref<GiftCardSummary | null>(null);
  const recipientFormRef = ref();
  const giftCardCodeRef = ref();
  const giftCardCode = ref("");
  const locationStatusData = ref<{
    name: string;
    status: string;
    close_message: string;
    identifier: string;
  }>({
    name: "",
    status: "",
    close_message: "",
    identifier: "",
  });

  const clearBooking = () => {
    booking.value.id = null;
    booking.value.bookedServices = [];
    booking.value.subTotal = 0;
    booking.value.date = business.value?.current_date || "";
    booking.value.selectedServices = [];
    booking.value.tapToken = "";
    booking.value.summary = null;
    booking.value.paymentMethodId = null;
    selectedPaymentMethod.value = null;
    invoice.value = null;
    selectedEmployee.value = null;
    bookingSource.value = null;
    packageEmployees.value = {};
    packageMemberEmployees.value = {};
  };

  const clearMembership = () => {
    selectedMembership.value = null;
    membershipSummary.value = null;
    canRefreshMemberships.value = true;
    couponApplied.value = false;
    deal.value = null;
    selectedPaymentMethod.value = null;
    invoice.value = null;
  };

  const clearCart = () => {
    cartCount.value = 0;
    // Forget where the count came from too, so it is loaded afresh next time.
    cartCountLocation.value = null;
    cartTotal.value = "0.000";
    goToOrderSummary.value = false;
    goToOrderCheckout.value = false;
    orderSummaryDetails.value = undefined;
    finalTotalAfterCoupon.value = null;
    couponApplied.value = false;
    deal.value = null;
    selectedPaymentMethod.value = null;
    invoice.value = null;
  };

  const clearGiftCard = () => {
    giftCardSelected.value = null;
    canRefreshGiftCards.value = true;
    currentStep.value = "choose_amount";
    goToGiftCardSummary.value = false;
    goToGiftCardCheckout.value = false;
    giftCardSummary.value = null;
    giftCardCode.value = "";
    couponApplied.value = false;
    deal.value = null;
    finalTotalAfterCoupon.value = null;
    selectedPaymentMethod.value = null;
    invoice.value = null;
  };

  const setRefreshStatus = (
    module: "services" | "memberships" | "times" | "confirm" | "giftCards",
    status: boolean
  ) => {
    switch (module) {
      case "services":
        canRefreshServices.value = status;
        break;
      case "memberships":
        canRefreshMemberships.value = status;
        break;
      case "times":
        canRefreshTimes.value = status;
        break;
      case "confirm":
        canRefreshConfirm.value = status;
        break;
      case "giftCards":
        canRefreshGiftCards.value = status;
        break;
    }
  };

  const resetRefreshStatus = (status: boolean = true) => {
    canRefreshServices.value = status;
    canRefreshMemberships.value = status;
    canRefreshTimes.value = status;
    canRefreshConfirm.value = status;
    canRefreshGiftCards.value = status;
  };

  const openMenu = () => {
    isMenuOpen.value = !isMenuOpen.value;
  };

  const openSearch = () => {
    isSearchOpen.value = !isSearchOpen.value;
  };

  function searchServiceByName() {
    const results = <typeof availableCategories.value>[];
    categories.value.forEach((category: ServiceCategory) => {
      const matchingServices = category.services.filter((service: Service) => {
        return (
          search.value === "" ||
          (service.name &&
            service.name.toLowerCase().includes(search.value.toLowerCase()))
        );
      });
      if (matchingServices.length > 0) {
        results.push({ ...category, services: matchingServices });
      }
    });
    availableCategories.value = results;
  }

  function searchProductByName() {
    if (search.value === "") {
      availableProductCategories.value = productCategories.value;
      return;
    }
    const results = <typeof availableProductCategories.value>[];
    productCategories.value.forEach((category: ProductCategory) => {
      const matchingProducts = category.products.filter((product: Product) => {
        return (
          product.name &&
          product.name.toLowerCase().includes(search.value.toLowerCase())
        );
      });
      if (matchingProducts.length > 0) {
        results.push({ ...category, products: matchingProducts });
      }
    });
    availableProductCategories.value = results;
  }

  const clearSearch = () => {
    search.value = "";
    searchServiceByName();
    searchProductByName();
  };

  function $reset() {
    business.value = null;
    isMenuOpen.value = false;
    isSearchOpen.value = false;
    selectedLocation.value = null;
    employees.value = [];
    selectedEmployee.value = null;
    search.value = "";
    default_js_formats.value = null;
    resetRefreshStatus();
    goToCheckout.value = false;
    cancelBookingOpen.value = false;
    canceledBooking.value = null;
    bookings.value = [];
    selectedPaymentMethod.value = null;
    invoice.value = null;
    creatingNewBooking.value = false;
    tapLoading.value = false;
    isCardValid.value = false;
    isNavigating.value = false;
    memberships.value = [];
    backToMemberships.value = false;
    selectedMembership.value = null;
    goToCheckoutMembership.value = false;
    payMembershipLoading.value = false;
    clientMemberships.value = [];
    goToOrderSummary.value = false;
    payOrderLoading.value = false;
    couponApplied.value = false;
    deal.value = null;
    goToOrderCheckout.value = false;
    giftCards.value = [];
    giftCardSelected.value = null;
    currentStep.value = "choose_amount";
    goToGiftCardSummary.value = false;
    goToGiftCardCheckout.value = false;
    giftCardSummary.value = null;
    recipientFormRef.value = null;
    giftCardCodeRef.value = null;
    goToGiftCardCheckout.value = false;
    payGiftCardLoading.value = false;
    giftCardCode.value = "";
    locationStatusData.value = {
      name: "",
      status: "",
      close_message: "",
      identifier: "",
    };
    clearBooking();
    clearCart();
    clearMembership();
    clearGiftCard();
  }

  return {
    business,
    isMenuOpen,
    isSearchOpen,
    selectedLocation,
    backToProfile,
    employees,
    selectedEmployee,
    bookingSource,
    packageEmployees,
    packageMemberEmployees,
    categories,
    availableCategories,
    booking,
    calendar,
    search,
    default_js_formats,
    countries,
    canRefreshServices,
    goToCheckout,
    cancelBookingOpen,
    canceledBooking,
    bookings,
    selectedPaymentMethod,
    invoice,
    creatingNewBooking,
    tapLoading,
    isCardValid,
    isNavigating,
    memberships,
    backToMemberships,
    canRefreshMemberships,
    selectedMembership,
    membershipSummary,
    goToCheckoutMembership,
    payMembershipLoading,
    clientMemberships,
    canRefreshConfirm,
    canRefreshTimes,
    productCategories,
    availableProductCategories,
    cartCount,
    cartCountLocation,
    cartTotal,
    goToOrderSummary,
    orderSummaryDetails,
    payOrderLoading,
    couponApplied,
    deal,
    finalTotalAfterCoupon,
    goToOrderCheckout,
    giftCards,
    giftCardSelected,
    canRefreshGiftCards,
    currentStep,
    recipientFormRef,
    goToGiftCardCheckout,
    giftCardSummary,
    goToGiftCardSummary,
    payGiftCardLoading,
    giftCardCode,
    giftCardCodeRef,
    locationStatusData,
    openMenu,
    openSearch,
    clearBooking,
    clearMembership,
    clearCart,
    clearGiftCard,
    setRefreshStatus,
    resetRefreshStatus,
    searchServiceByName,
    searchProductByName,
    clearSearch,
    $reset,
  };
});
